import { axiosGlobalPayInstance, handleResponse, handleError } from './index';

const terminalPollIntervalMs = 2000;
// terminals wait up to 10 minutes for the customer, plus time for the server to save the result
const terminalPollTimeoutMs = 15 * 60 * 1000;

export async function createDraftTransaction(reqBody) {
  try {
    const response = await axiosGlobalPayInstance.post('/draft', reqBody);

    return handleResponse(response);
  } catch (err) {
    return handleError(err);
  }
}

export async function createTerminalSale(reqBody) {
  try {
    return await runTerminalRequest('/transactions/startTerminalSale', reqBody);
  } catch (err) {
    return handleError(err);
  }
}

/**
 * Terminal requests can take up to 10 minutes, longer than an API request can stay open.
 * The server starts the request in the background and we poll for the result, which is
 * the same response the terminal endpoints used to return directly.
 */
async function runTerminalRequest(startPath, reqBody) {
  await axiosGlobalPayInstance.post(startPath, reqBody);

  const response = await waitForTerminalRequest(reqBody.transactionId);

  return handleResponse(response);
}

async function waitForTerminalRequest(transactionId) {
  const deadline = Date.now() + terminalPollTimeoutMs;

  while (Date.now() < deadline) {
    await new Promise((resolve) => setTimeout(resolve, terminalPollIntervalMs));

    try {
      const response = await axiosGlobalPayInstance.get(
        `/transactions/terminalRequestStatus/${transactionId}`
      );

      if (response.data?.terminalRequestState === 'complete') return response;
    } catch (err) {
      if (isFinalTerminalRequestError(err)) throw err;

      // a failed status check doesn't mean the payment failed, so keep checking
      console.error('Error checking terminal request status, retrying: ', err);
    }
  }

  const err = new Error('Timed out waiting for the terminal request');
  err.errorMessage =
    'Lost contact with the terminal request. Check the transactions list before trying again.';
  throw err;
}

function isFinalTerminalRequestError(err) {
  const status = err.response?.status;

  return (
    // a failed payment result from the server, rather than a network or gateway error
    err.response?.data?.terminalRequestState === 'complete' ||
    err.message === 'User logged out' ||
    (status >= 400 && status < 500)
  );
}

export async function createPayFieldsSale(reqBody) {
  try {
    const response = await axiosGlobalPayInstance.post(
      '/transactions/createPayFieldsSale',
      reqBody
    );

    return handleResponse(response);
  } catch (err) {
    return handleError(err);
  }
}

export async function createSavedCardSale(reqBody) {
  try {
    const response = await axiosGlobalPayInstance.post(
      '/transactions/createSavedCardSale',
      reqBody
    );

    return handleResponse(response);
  } catch (err) {
    return handleError(err);
  }
}

export async function cancelTerminalSale(transactionId) {
  try {
    const response = await axiosGlobalPayInstance.put(
      '/transactions/cancelTerminalSale',
      { transactionId }
    );

    return handleResponse(response);
  } catch (err) {
    return handleError(err);
  }
}

export async function runCNPZeroDollarAuth(tempToken, billingAddress) {
  try {
    const response = await axiosGlobalPayInstance.post(
      '/transactions/runCNPZeroDollarAuth',
      { tempToken, billingAddress }
    );

    return handleResponse(response);
  } catch (err) {
    return handleError(err);
  }
}

export async function runTerminalZeroDollarAuth(deviceId, transactionId) {
  try {
    return await runTerminalRequest(
      '/transactions/startTerminalZeroDollarAuth',
      { deviceId, transactionId }
    );
  } catch (err) {
    return handleError(err);
  }
}

export async function deleteCardTokens(cardTokens) {
  try {
    // has to be post to have the body
    const response = await axiosGlobalPayInstance.post(
      '/cards/deleteCardTokens',
      { cardTokens }
    );

    return handleResponse(response);
  } catch (err) {
    return handleError(err);
  }
}

export async function processVoid(transactionId, voidReason) {
  try {
    const response = await axiosGlobalPayInstance.post('/transactions/void', {
      transactionId,
      voidReason,
    });

    return handleResponse(response);
  } catch (err) {
    return handleError(err);
  }
}
